import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserPublic } from '@veodiagram/shared';
import { api } from '../services/api';

// Recovery token localStorage key (Issue #164)
const RECOVERY_TOKEN_KEY = 'veodiagram_recovery_token';

// Helper functions for recovery token (stored separately from zustand persist)
export const getRecoveryToken = (): string | null => {
  try {
    return localStorage.getItem(RECOVERY_TOKEN_KEY);
  } catch {
    return null;
  }
};

export const setRecoveryToken = (token: string): void => {
  try {
    localStorage.setItem(RECOVERY_TOKEN_KEY, token);
  } catch {
    // Ignore localStorage errors
  }
};

export const clearRecoveryToken = (): void => {
  try {
    localStorage.removeItem(RECOVERY_TOKEN_KEY);
  } catch {
    // Ignore localStorage errors
  }
};

interface AuthState {
  user: UserPublic | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isRecovering: boolean; // True while attempting session recovery
  setAuth: (user: UserPublic, token: string, recoveryToken?: string) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  attemptRecovery: () => Promise<boolean>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,
      isRecovering: false,

      setAuth: (user, token, recoveryToken) => {
        // Store recovery token if provided (for local auth)
        if (recoveryToken) {
          setRecoveryToken(recoveryToken);
        }
        set({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
          isRecovering: false,
        });
      },

      logout: () => {
        // Clear recovery token on logout
        clearRecoveryToken();
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          isRecovering: false,
        });
        // Call logout API
        fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
      },

      setLoading: (loading) => set({ isLoading: loading }),

      // Attempt to recover session using stored recovery token (Issue #164)
      attemptRecovery: async () => {
        const recoveryToken = getRecoveryToken();
        if (!recoveryToken) {
          return false;
        }

        // Don't attempt if already authenticated
        if (get().isAuthenticated) {
          return true;
        }

        set({ isRecovering: true });

        try {
          const response = await api.recoverSession(recoveryToken);
          if (response.success && response.user) {
            set({
              user: response.user as UserPublic,
              token: response.token,
              isAuthenticated: true,
              isLoading: false,
              isRecovering: false,
            });
            return true;
          }
        } catch {
          // Recovery failed - token is invalid or expired
          clearRecoveryToken();
        }

        set({ isRecovering: false });
        return false;
      },
    }),
    {
      name: 'veodiagram-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
